<template>
  <img
    v-if="entry"
    v-bind="$attrs"
    :class="{ 'cursor-zoom-in': useLightBox }"
    :src="url"
    :alt="title"
    :width="imageWidth"
    :height="imageHeight"
    @click="showLightbox = true"
  />
  <LazyCLightbox
    v-if="showLightbox && useLightBox"
    :style="styles"
    :images="[{ assetUrl: internalUrl }]"
    :params="lightBoxParams"
    @close="showLightbox = false"
  />
</template>

<script setup lang="ts">
import type { ContentfulImageMinimal, ContentfulImageType } from '@/types/contentful';

type Props = {
  entry?: ContentfulImageType | ContentfulImageMinimal;
  width?: number;
  height?: number;
  fit?: string;
  format?: 'webp' | 'png' | 'jpg';
  quality?: string;
  useLightBox?: boolean;
  alt?: string;
};

type Image = {
  width?: number;
  height?: number;
};

type QueryParams = {
  height?: string;
  width?: string;
  fit?: string;
  format?: string;
  quality?: string;
};

const props = withDefaults(defineProps<Props>(), {
  entry: undefined,
  width: undefined,
  height: undefined,
  fit: '',
  format: 'webp',
  quality: '85',
  useLightBox: false,
  alt: undefined,
});

const showLightbox = ref(false);

const title = computed(() => {
  return (
    props?.alt ??
    ((props.entry as ContentfulImageType)?.fields?.title || (props.entry as ContentfulImageMinimal)?.title)
  );
});

const image = computed<Image>(() => {
  return (props.entry as ContentfulImageType)?.fields?.file?.details?.image || props.entry || {};
});
const internalUrl = computed(() => {
  return (props.entry as ContentfulImageType)?.fields?.file?.url || (props.entry as ContentfulImageMinimal)?.url;
});
const isSvg = computed(() => {
  return internalUrl.value.endsWith('.svg');
});
const styles = computed(() => {
  if (!isSvg.value) return null;
  return `width: ${image.value.width}px`;
});
const lightBoxParams = computed(() => {
  if (isSvg.value) return '';
  const query = new URLSearchParams();
  const params = {
    fit: props.fit,
    q: props.quality,
    fm: props.format,
    w: imageWidth.value?.toString(),
    h: imageHeight.value?.toString(),
  };
  for (const [key, cfKey] of Object.entries(params)) {
    if (cfKey) query.set(key, cfKey);
  }
  return `?${query}`;
});
const url = computed(() => {
  if (!internalUrl.value) return;

  let params: { height: string; width: string; fit: string; format?: string; quality: string } = {
    height: 'h',
    width: 'w',
    fit: 'fit',
    quality: 'q',
  };

  if (!isSvg.value) {
    params = { ...params, format: 'fm', quality: 'q' };
  }

  const query = new URLSearchParams();
  for (const [key, cfKey] of Object.entries(params)) {
    if (props[key as keyof QueryParams]) {
      query.set(cfKey, String(props[key as keyof QueryParams]));
    }
  }
  return internalUrl.value + (query && '?' + query);
});

const imageWidth = computed(() => {
  return props.width ? props.width : image.value?.width;
});

const imageHeight = computed(() => {
  return props.height ? props.height : image.value?.height;
});
</script>

<script lang="ts">
export default defineComponent({
  name: 'ContentfulImage',
});
</script>
